import { useEffect, useState, useCallback } from "react";
import { api, rupee } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit } from "lucide-react";

const empty = { name: "", sku: "", category: "Handbags", price: 0, discount_price: null, description: "", material: "", dimensions: "", colors: [], sizes: ["Standard"], stock: 0, images: [], featured: false, bestseller: false, new_arrival: true, enabled: true, low_stock_threshold: 5 };

export default function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/admin/products").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (p) => {
    try { await api.put(`/admin/products/${p.id}`, { enabled: !p.enabled }); toast.success(p.enabled ? "Hidden from store (data kept)" : "Visible on store"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const save = async () => {
    try {
      const payload = { ...form, price: +form.price, discount_price: form.discount_price ? +form.discount_price : null, stock: +form.stock, colors: (form.colors||"").toString().split(",").map(s=>s.trim()).filter(Boolean), images: (form.images||"").toString().split(",").map(s=>s.trim()).filter(Boolean) };
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };
  const edit = (p) => { setEditing(p.id); setForm({ ...p, colors: (p.colors||[]).join(","), images: (p.images||[]).join(",") }); };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h1 className="font-serif text-3xl">Products</h1>
        <button onClick={() => { setEditing(null); setForm(empty); }} className="bg-[#C5A059] text-[#121212] px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="admin-product-new"><Plus size={14} /> New</button>
      </div>

      <div className="admin-surface p-6 mb-6">
        <p className="eyebrow mb-4">{editing ? "Edit" : "Create"} Product</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[["name","Name"],["sku","SKU"],["category","Category"],["material","Material"],["dimensions","Dimensions"]].map(([k,l]) => (
            <input key={k} placeholder={l} value={form[k] || ""} onChange={(e) => setForm({...form,[k]:e.target.value})} className="admin-input" data-testid={`admin-prod-${k}`} />
          ))}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8C867D] mb-1">MRP (₹)</label>
            <input type="number" placeholder="MRP" value={form.price} onChange={(e) => setForm({...form,price:e.target.value})} className="admin-input w-full" data-testid="admin-prod-price" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8C867D] mb-1">Discount Price (₹)</label>
            <input type="number" placeholder="Discount Price" value={form.discount_price ?? ""} onChange={(e) => setForm({...form,discount_price:e.target.value||null})} className="admin-input w-full" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8C867D] mb-1">Stock</label>
            <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({...form,stock:e.target.value})} className="admin-input w-full" data-testid="admin-prod-stock" />
          </div>
          <input placeholder="Colors (comma-separated)" value={form.colors} onChange={(e) => setForm({...form,colors:e.target.value})} className="admin-input" />
          <input placeholder="Image URLs (comma-separated)" value={form.images} onChange={(e) => setForm({...form,images:e.target.value})} className="admin-input col-span-2" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form,description:e.target.value})} className="admin-input col-span-2" rows={3} />
          <div className="col-span-2 flex gap-6 text-xs">
            {["featured","bestseller","new_arrival","enabled"].map((f) => (
              <label key={f} className="flex items-center gap-2"><input type="checkbox" checked={!!form[f]} onChange={(e) => setForm({...form,[f]:e.target.checked})} /> {f}</label>
            ))}
          </div>
          <button onClick={save} className="col-span-2 bg-[#C5A059] text-[#121212] py-2 text-xs uppercase tracking-widest" data-testid="admin-prod-save">Save Product</button>
        </div>
      </div>

      <div className="admin-surface">
        <table className="w-full text-sm">
          <thead className="text-[#8C867D] text-xs uppercase tracking-widest text-left">
            <tr><th className="p-3">SKU</th><th>Name</th><th>Category</th><th>Price</th><th>Stock</th><th>Visible</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[#27272A]">
                <td className="p-3 font-mono text-xs">{p.sku}</td>
                <td>{p.name}</td>
                <td className="text-xs">{p.category}</td>
                <td>{rupee(p.discount_price || p.price)}</td>
                <td className={p.stock <= 5 ? "text-[#C5A059]" : ""}>{p.stock}</td>
                <td>
                  <button onClick={() => toggleVisible(p)} className={`w-10 h-5 rounded-full relative transition ${p.enabled !== false ? "bg-[#C5A059]" : "bg-[#27272A]"}`} data-testid={`admin-prod-toggle-${p.sku}`} title={p.enabled !== false ? "Visible on store — click to hide" : "Hidden — click to show"}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${p.enabled !== false ? "left-5" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="text-right pr-3">
                  <button onClick={() => edit(p)} className="mr-3" data-testid={`admin-prod-edit-${p.sku}`}><Edit size={14} /></button>
                  <button onClick={() => del(p.id)} data-testid={`admin-prod-del-${p.sku}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
